/**
 * Copyright (c) 2018-2019 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { computeRings, getFingerprint, createIndex } from './rings/compute';
import { SortedArray } from '../../../../mol-data/int';
import { BondType } from '../../model/types';
import { getPositions } from '../../util';
import { PrincipalAxes } from '../../../../mol-math/linear-algebra/matrix/principal-axes';
import { Vec3 } from '../../../../mol-math/linear-algebra';

var UnitRings =
/** @class */
function () {
  function UnitRings(all, unit) {
    this.unit = unit;
    this.all = all;
  }

  Object.defineProperty(UnitRings.prototype, "index", {
    get: function () {
      if (this._index) return this._index;
      this._index = createIndex(this.all, this.aromaticRings);
      return this._index;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(UnitRings.prototype, "byFingerprint", {
    get: function () {
      if (this._byFingerprint) return this._byFingerprint;
      this._byFingerprint = createByFingerprint(this.unit, this.all);
      return this._byFingerprint;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(UnitRings.prototype, "elementRingIndices", {
    /** Maps atom index inside a Unit to ring indices (an atom can be part of more than one ring) */
    get: function () {
      return this.index.elementRingIndices;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(UnitRings.prototype, "elementAromaticRingIndices", {
    get: function () {
      return this.index.elementAromaticRingIndices;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(UnitRings.prototype, "ringComponentIndex", {
    /** Maps UnitRings.Index to index to ringComponents */
    get: function () {
      return this.index.ringComponentIndex;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(UnitRings.prototype, "ringComponents", {
    get: function () {
      return this.index.ringComponents;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(UnitRings.prototype, "aromaticRings", {
    get: function () {
      if (this._aromaticRings) return this._aromaticRings;
      this._aromaticRings = getAromaticRings(this.unit, this.all);
      return this._aromaticRings;
    },
    enumerable: false,
    configurable: true
  });
  return UnitRings;
}();

var UnitRing;

(function (UnitRing) {
  function fingerprint(unit, ring) {
    var elements = unit.elements;
    var type_symbol = unit.model.atomicHierarchy.atoms.type_symbol;
    var symbols = [];

    for (var i = 0, _i = ring.length; i < _i; i++) symbols[symbols.length] = type_symbol.value(elements[ring[i]]);

    return elementFingerprint(symbols);
  }

  UnitRing.fingerprint = fingerprint;

  function elementFingerprint(elements) {
    return getFingerprint(elements);
  }

  UnitRing.elementFingerprint = elementFingerprint;
  var AromaticRingElements = new Set(["B"
  /* B */
  , "C"
  /* C */
  , "N"
  /* N */
  , "O"
  /* O */
  , "SI"
  /* SI */
  , "P"
  /* P */
  , "S"
  /* S */
  , "GE"
  /* GE */
  , "AS"
  /* AS */
  , "SN"
  /* SN */
  , "SB"
  /* SB */
  , "BI"
  /* BI */
  ]);
  var AromaticRingPlanarityThreshold = 0.05;

  function isAromatic(unit, ring) {
    var elements = unit.elements,
        _a = unit.bonds,
        b = _a.b,
        offset = _a.offset,
        flags = _a.edgeProps.flags;
    var _b = unit.model.atomicHierarchy.atoms,
        type_symbol = _b.type_symbol,
        label_comp_id = _b.label_comp_id; // ignore Proline (can be flat because of bad geometry)

    if (label_comp_id.value(unit.elements[ring[0]]) === 'PRO') return false;
    var aromaticBondCount = 0;
    var hasAromaticRingElement = false;

    for (var i = 0, il = ring.length; i < il; ++i) {
      var aI = ring[i];

      if (!hasAromaticRingElement && AromaticRingElements.has(type_symbol.value(elements[aI]))) {
        hasAromaticRingElement = true;
      }

      for (var j = offset[aI], jl = offset[aI + 1]; j < jl; ++j) {
        // comes e.g. from `chem_comp_bond.pdbx_aromatic_flag`
        if (BondType.is(16
        /* Aromatic */
        , flags[j])) {
          if (SortedArray.has(ring, b[j])) aromaticBondCount += 1;
        }
      }
    }

    if (aromaticBondCount === 2 * ring.length) return true;
    if (!hasAromaticRingElement) return false;
    var ma = PrincipalAxes.calculateMomentsAxes(getPositions(unit, ring));
    return Vec3.magnitude(ma.dirC) < AromaticRingPlanarityThreshold;
  }

  UnitRing.isAromatic = isAromatic;
  /** Get the alternate location of the 1st non '' alt loc atom. */

  function getAltId(unit, ring) {
    var label_alt_id = unit.model.atomicHierarchy.atoms.label_alt_id;
    var elements = unit.elements;

    for (var i = 0, il = ring.length; i < il; ++i) {
      var eI = elements[ring[i]];
      var altId = label_alt_id.value(eI);
      if (altId) return altId;
    }

    return '';
  }

  UnitRing.getAltId = getAltId;
})(UnitRing || (UnitRing = {}));

(function (UnitRings) {
  function create(unit) {
    var rings = computeRings(unit);
    return new UnitRings(rings, unit);
  }

  UnitRings.create = create;
  /** Creates a mapping ResidueIndex -> list or rings that are on that residue and have one of the specified fingerprints. */

  function byFingerprintAndResidue(rings, fingerprints) {
    var map = new Map();

    for (var fI = 0, _fI = fingerprints.length; fI < _fI; fI++) {
      var fp = fingerprints[fI];
      addSingleResidueRings(rings, fp, map);
    }

    return map;
  }

  UnitRings.byFingerprintAndResidue = byFingerprintAndResidue;
})(UnitRings || (UnitRings = {}));

function createByFingerprint(unit, rings) {
  var byFingerprint = new Map();
  var idx = 0;

  for (var rI = 0, _rI = rings.length; rI < _rI; rI++) {
    var r = rings[rI];
    var fp = UnitRing.fingerprint(unit, r);
    if (byFingerprint.has(fp)) byFingerprint.get(fp).push(idx);else byFingerprint.set(fp, [idx]);
    idx++;
  }

  return byFingerprint;
}

function ringResidueIdx(unit, ring) {
  var elements = unit.elements;
  var residueIndex = unit.model.atomicHierarchy.residueAtomSegments.index;
  var idx = residueIndex[elements[ring[0]]];

  for (var rI = 1, _rI = ring.length; rI < _rI; rI++) {
    if (idx !== residueIndex[elements[ring[rI]]]) return -1;
  }

  return idx;
}

function addSingleResidueRings(rings, fp, map) {
  var byFp = rings.byFingerprint.get(fp);
  if (!byFp) return;

  for (var rI = 0, _rI = byFp.length; rI < _rI; rI++) {
    var r = byFp[rI];
    var idx = ringResidueIdx(rings.unit, rings.all[r]);

    if (idx >= 0) {
      if (map.has(idx)) map.get(idx).push(r);else map.set(idx, [r]);
    }
  }
}

function getAromaticRings(unit, rings) {
  var aromaticRings = [];

  for (var i = 0, il = rings.length; i < il; ++i) {
    if (UnitRing.isAromatic(unit, rings[i])) aromaticRings.push(i);
  }

  return aromaticRings;
}

export { UnitRing, UnitRings };